import {TranslateService} from '@ngx-translate/core';

import {DashboardChartUtils} from './dashboard-chart.utils';

import {ChartUtils} from '../../+postpayroll/check/key-metrics/charts/chart.utils';
import {BasicLine} from '../../+postpayroll/check/key-metrics/charts/line/basic-line';
import {Chart} from '../../+postpayroll/check/key-metrics/key-metrics.interfaces';
import {LocalizationService} from '../../shared/localization/localization.service';
import {DashboardBox, GrossPayChartBoxElement, ResponseChartBox} from '../dashboard.interfaces';

export class GrossSalaryChart extends BasicLine {
  constructor(chartBox: DashboardBox,
              translate: TranslateService,
              localization: LocalizationService,
              isAvg: boolean) {
    const params = chartBox.params;
    const chart: Chart = ChartUtils.getEmptyChart();

    if (Object.keys(params).length) {
      const selectedRegulation = chartBox.selectedRegulation || Object.keys(params)[0]; // Default to the first regulation if none is selected
      chartBox.params[selectedRegulation].forEach((element: GrossPayChartBoxElement) => {
        chart.data.push({
          category: selectedRegulation,
          name: element.name,
          value: element.value?.TotalMonthlySalary,
          metadata: {
            ...element.value,
            regulation: selectedRegulation,
            currency: chartBox.currency,
            currentMonthGrossSalary: chartBox.currentMonthGrossSalary
          }
        });
      });
    }

    chart.type = chartBox.cardType;

    super(chart, translate, localization, isAvg);

    this.configureAxis(translate);
    this.configureGrid();
    this.configureTooltip(translate);
    this.configureLegend();
    this.configureChart(chartBox);
  }

  private configureAxis(translate: TranslateService): void {
    this.xAxis = [{
      data: DashboardChartUtils.formatCategories(this.xAxis[0].data, translate),
      axisLabel: DashboardChartUtils.getAxisOptions(),
      axisTick: {
        show: true,
        alignWithLabel: true,
      }
    }];

    this.yAxis = [{
      axisLine: {
        show: true
      },
      axisTick: {
        show: true,
        alignWithLabel: true,
      }
    }];

    this.yAxis[0].axisLabel = {
      ...this.yAxis[0].axisLabel,
      formatter: (value: number) => this.formatSalary(value)
    };
  }

  private configureGrid(): void {
    this.grid = {
      ...DashboardChartUtils.getGridOptions(),
      left: 10,
      right: 10,
      top: 20,
      bottom: 80,
      containLabel: true
    };
    this.animation = true;
  }

  private configureTooltip(translate: TranslateService): void {
    this.tooltip.formatter = (tooltipParams: any) => {
      const param = Array.isArray(tooltipParams) ? tooltipParams[0] : tooltipParams;
      const month = param.name;
      const regulation = param.data.metadata?.regulation || param.seriesName;
      const activeContracts = `${param.data.metadata?.ActiveContracts} ${translate.instant('DASHBOARD.active_contracts')}`;
      const totalMonthlySalary = this.localization.localizeNumber(param.data.metadata?.TotalMonthlySalary, 'CURRENCY_2_DIGIT');
      const totalSalary = `${translate.instant('DASHBOARD.total_gross_salary')}: ${totalMonthlySalary}`;

      return `${month}<br/>
              &nbsp;&nbsp;${regulation}<br/>
              &nbsp;&nbsp;${activeContracts}  <br/>
              &nbsp;&nbsp;${totalSalary}`;
    };
  }

  private configureLegend(): void {
    this.legend = {
      ...this.legend,
      show: false,
      selectedMode: 'single',
      right: 0,
      bottom: 0,
      textStyle: {
        rich: {
          title: {
            fontSize: 12,
            fontWeight: 'bold',
            padding: [0, 0, 5, 0]
          },
          subtitle: {
            fontSize: 10,
            color: '#666',
            padding: [0, 0, 10, 0]
          }
        }
      },
      padding: [0, 0, 0, 0]
    };
  }

  private configureChart(chartBox: ResponseChartBox): void {
    DashboardChartUtils.setLinks(this.series, this.chart, this.categories);
    this.tooltip.position = DashboardChartUtils.getTooltipPositionFunc(<DashboardBox>chartBox);

    this.color = DashboardChartUtils.standardColors();
  }

  public sortCategories(array: string[]): string[] {
    return DashboardChartUtils.monthSort(array);
  }

  private formatSalary(value: number): string {
    if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + 'M';
    } else if (value >= 1000) {
      return (value / 1000).toFixed(0) + 'K';
    }
    return value.toString();
  }
}
